import { FC } from 'react'

import { t } from 'translations'

// Component to display an address in "label: key" format
type DisplayTextLabel = {
  address: MData.Address
  icon?: boolean
}

const DisplayTextLabel: FC<DisplayTextLabel> = ({ address, icon }) => {
  const { administrative_area_level_1, country, floor, locality, postal_code, room, route, street_number } = address
  return (
    <>
      {room ? (
        <span>
          <b>{t('components.address.ROOM')}: </b>
          {room}{' '}
        </span>
      ) : null}
      {floor ? (
        <span>
          <b>{t('components.address.FLOOR')}: </b>
          {floor}
        </span>
      ) : null}
      {room || floor ? <br /> : null}
      {street_number ? (
        <span>
          <b>{t('components.address.STREET_ADDRESS')}: </b>
          {street_number}{' '}
        </span>
      ) : null}
      {route ? <span>{route}</span> : null}

      {street_number || route ? <br /> : null}

      {locality ? (
        <span>
          <b>{t('components.address.CITY')}: </b>
          {locality}
          <br />
        </span>
      ) : null}
      {administrative_area_level_1 ? (
        <span>
          <b>{t('components.address.STATE')}: </b>
          {administrative_area_level_1}{' '}
        </span>
      ) : null}
      {postal_code ? (
        <span>
          <b>{t('components.address.ZIP_CODE')}: </b>
          {postal_code}
        </span>
      ) : null}
      {administrative_area_level_1 || postal_code ? (
        <span>
          <br />
        </span>
      ) : null}
      {country ? (
        <span>
          <b>{t('components.address.COUNTRY')}: </b>
          {country}
          <br />
        </span>
      ) : null}
      {icon ? <i className={`far fa-external-link-alt fa-sm ml-2`} /> : null}
    </>
  )
}

type DisplayTextOnlyProps = {
  address: MData.Address
  icon?: boolean
}

// Component to display only the address as text (ex: 977 Rue Sainte-Catherine Ouest, Salle: 2 Étage: 3, Montréal, QC H3B 4W3, Canada)
const DisplayTextOnly: FC<DisplayTextOnlyProps> = ({ address, icon }) => {
  const { administrative_area_level_1, country, floor, locality, postal_code, room, route, street_number } = address

  return (
    <span>
      {street_number ? <span>{street_number} </span> : null}
      {route ? <span>{route}</span> : null}
      {street_number || route ? <span>, </span> : null}
      {room ? (
        <span>
          {t('components.address.ROOM')}: {room}
        </span>
      ) : null}
      {room && floor ? <span>, </span> : null}
      {floor ? (
        <span>
          {t('components.address.FLOOR')}: {floor}
        </span>
      ) : null}
      {room || floor ? <span>, </span> : null}
      {locality ? <span>{locality}, </span> : null}
      {administrative_area_level_1 ? <span>{administrative_area_level_1} </span> : null}
      {postal_code ? <span>{postal_code}</span> : null}
      {administrative_area_level_1 || postal_code ? <span>,</span> : null}
      {country ? <span> {country}</span> : null}
      {icon ? <i className={`far fa-external-link-alt fa-sm ml-2`} /> : null}
    </span>
  )
}

// Component to display only the address as text (ex: 977 Rue Sainte-Catherine Ouest, Salle: 2 Étage: 3, Montréal, QC H3B 4W3, Canada)

type DisplayMinimumOnlyProps = {
  address: MData.Address
  icon?: boolean
}

const DisplayMinimumOnly: FC<DisplayMinimumOnlyProps> = ({ address, icon }) => {
  const { floor, locality, room, route, street_number } = address
  return (
    <span>
      {street_number ? <span>{street_number} </span> : null}
      {route ? <span>{route}</span> : null}
      {street_number || route ? <span>, </span> : null}
      {room ? (
        <span>
          {t('components.address.ROOM')}: {room}{' '}
        </span>
      ) : null}
      {floor ? (
        <span>
          {t('components.address.FLOOR')}: {floor}
        </span>
      ) : null}
      {room || floor ? <span>,</span> : null}
      {locality ? <span>{locality}</span> : null}
      {icon ? <i className={`far fa-external-link-alt fa-sm ml-2`} /> : null}
    </span>
  )
}

type Props = {
  address?: MData.Address
  className?: string
  icon?: boolean
  minimum?: boolean
  short?: boolean
  withLink?: boolean
}

const Address: FC<Props> = ({ address, short, minimum, withLink, icon, className = 'text-dark' }) => {
  const getAddress = ({ locality, postal_code, route, street_number }: MData.Address): string =>
    `https://maps.google.com/?daddr=${street_number} ${route}, ${locality}, ${postal_code}`

  if (!address) return <>{t('components.NO_ADDRESS')}</>

  if (withLink && short)
    return (
      <a href={getAddress(address)} target="_blank" rel="noreferrer" className={className}>
        <DisplayTextOnly address={address} icon={icon} />
      </a>
    )
  if (withLink && minimum)
    return (
      <a href={getAddress(address)} target="_blank" rel="noreferrer" className={className}>
        <DisplayMinimumOnly address={address} icon={icon} />
      </a>
    )
  return (
    <>
      {/* One liner view of the address */}
      {short ? <DisplayTextOnly address={address} icon={icon} /> : <DisplayTextLabel address={address} icon={icon} />}
    </>
  )
}

export default Address
